import type { TFunction } from "i18next";
import moment, { type Moment } from "moment";
import {
  ContractDurationType,
  ContractNature,
  type ContractDurationDto,
  type CpvCodeDto,
  ProcedureType,
} from "../../../../autogen/bff-api";
import { displayDate, type SupportedLocale } from "../../../../common/formatting/displayDate";
import { displayDateWithTime } from "../../../../common/formatting/displayDateWithTime";
import { formatTimeLeft, timeLeftUntil, type TimeLeft } from "../../../../common/formatting/timeLeft";

export const getBuyerInitials = (buyerName: string) =>
  buyerName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

const timeLeftColor = (timeLeft: TimeLeft): string => {
  if (timeLeft.unit === "passed") return "smMuted";
  if (timeLeft.unit === "daysAndHours" && timeLeft.days > 2) return "smMuted";
  return "smError";
};

export const getTimeLeftMeta = (deadline: string, t: TFunction, now?: Moment) => {
  const timeLeft = timeLeftUntil(deadline, now);
  return { timeLeft, label: formatTimeLeft(timeLeft, t), color: timeLeftColor(timeLeft) };
};

export const formatCpv = (cpv: CpvCodeDto, language: string) =>
  `${cpv.code} - ${language.startsWith("no") ? cpv.nameNb : cpv.nameEn}`;

export const getProcedureLabel = (procedureType: ProcedureType, t: TFunction) => t(procedureType);

export const getContractNatureLabel = (contractNature: ContractNature, t: TFunction) => {
  switch (contractNature) {
    case ContractNature.Supplies:
      return t("Goods");
    case ContractNature.Services:
      return t("Services");
    case ContractNature.Works:
      return t("Construction");
    case ContractNature.Combined:
      return t("Combined");
  }
};

export const formatEstimatedValue = (value: number, currency: string) =>
  `${value.toLocaleString("nb-NO")} ${currency}`;

export const formatShortDate = (value: string, locale?: SupportedLocale | null) =>
  displayDate(value, locale);

export const formatDeadline = (value: string, at: string, locale?: SupportedLocale | null) =>
  displayDateWithTime(value, at, locale);

export const deriveContractEndDate = (
  duration: ContractDurationDto | null | undefined,
): string | null => {
  if (!duration) return null;
  if (duration.type === ContractDurationType.FixedDates) return duration.endDate ?? null;
  if (duration.type === ContractDurationType.PeriodBased && duration.startDate && duration.periodMonths != null) {
    return moment(duration.startDate).add(duration.periodMonths, "months").format("YYYY-MM-DD");
  }
  return null;
};

export const formatContractDuration = (
  duration: ContractDurationDto | null | undefined,
  t: TFunction,
  locale?: SupportedLocale | null,
): string => {
  if (!duration) return "-";
  if (duration.type === ContractDurationType.OpenEnded) {
    return duration.startDate
      ? t("Running agreement from {{date}}", { date: displayDate(duration.startDate, locale) })
      : t("Running agreement");
  }
  if (duration.type === ContractDurationType.FixedDates) {
    return duration.startDate && duration.endDate
      ? `${displayDate(duration.startDate, locale)} - ${displayDate(duration.endDate, locale)}`
      : "-";
  }
  if (duration.type === ContractDurationType.PeriodBased) {
    if (duration.periodMonths == null) return "-";
    const months = t("month", { count: duration.periodMonths });
    if (!duration.startDate) return `${duration.periodMonths} ${months}`;
    const derivedEndDate = deriveContractEndDate(duration);
    if (derivedEndDate == null) {
      return `${displayDate(duration.startDate, locale)} + ${duration.periodMonths} ${months}`;
    }
    return `${displayDate(duration.startDate, locale)} + ${duration.periodMonths} ${months} (${t("ends {{date}}", {
      date: displayDate(derivedEndDate, locale),
    })})`;
  }
  return "-";
};
